<template>
  <div class="container-fluid page-container">
    <spinner v-if="busy" />
    <div v-else>
      <div class="row page-header__container mb-4 pb-3 justify-content-between">
        <page-heading :class="'col-4'" :title="pageTitle" />
        <div class="col col-8 d-flex call-info justify-content-end">
          <span v-if="businessUnitContactPhone">
            <a :href="`tel: ${businessUnitContactPhone}`" class="btn btn-sm btn-secondary float-right separator">
              <font-awesome-icon icon="phone" rotation="90" />
              {{ $t('Call Us') }}
            </a>
          </span>
          <h2 class="my-0">
            <a :href="`tel: ${businessUnitContactPhone}`">{{ businessUnitContactPhone }}</a>
          </h2>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <form class="bg-white rounded shadow p-4" @submit.prevent="onFormSubmit">
            <validation-observer ref="validationObserver">
              <div class="row">
                <div class="col-12 col-lg-6 order-1 order-lg-0">
                  <div class="row">
                    <div class="col-12 col-lg-12">
                      <div class="row">
                        <div class="col-6">
                          <validation-provider v-slot="{valid, invalid, errors, dirty, validated}" rules="required">
                            <b-form-group :label="$t('First Name *')" label-for="name-input">
                              <b-form-input
                                id="firstname-input"
                                v-model="firstName"
                                trim
                                :state="(dirty || validated) ? (valid ? null : false) : null"
                              />
                              <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                                {{ errors[0] }}
                              </b-form-invalid-feedback>
                            </b-form-group>
                          </validation-provider>
                        </div>
                        <div class="col-6">
                          <validation-provider v-slot="{valid, invalid, errors, dirty, validated}" rules="required">
                            <b-form-group :label="$t('Last Name *')" label-for="name-input">
                              <b-form-input
                                id="lastname-input"
                                v-model="lastName"
                                required
                                trim
                                :state="(dirty || validated) ? (valid ? null : false) : null"
                              />
                            </b-form-group>
                            <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                              {{ errors[0] }}
                            </b-form-invalid-feedback>
                          </validation-provider>
                        </div>
                      </div>
                      <validation-provider v-slot="{valid, invalid, errors, dirty, validated}" rules="required|email">
                        <b-form-group :label="$t('Email Address *')" label-for="email-input">
                          <b-form-input
                            id="email-input"
                            v-model="email"
                            type="email"
                            trim
                            :state="(dirty || validated) ? (valid ? null : false) : null"
                          />
                        </b-form-group>
                        <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </validation-provider>
                      <validation-provider v-slot="{valid, invalid, errors, dirty, validated}" rules="required|is-phone-number">
                        <b-form-group :label="$t('Contact Number *')" label-for="contact-number-input">
                          <b-form-input
                            id="contact-number-input"
                            v-model="contactNumber"
                            required
                            type="tel"
                            trim
                            :state="(dirty || validated) ? (valid ? null : false) : null"
                          />
                        </b-form-group>
                        <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </validation-provider>
                      <validation-provider v-slot="{valid, invalid, errors, dirty, validated}" rules="required">
                        <b-form-group :label="$t('Company Name *')" label-for="company-name-input">
                          <b-form-input
                            id="company-name-input"
                            v-model="companyName"
                            required
                            trim
                            :state="(dirty || validated) ? (valid ? null : false) : null"
                          />
                        </b-form-group>
                        <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                          {{ errors[0] }}
                        </b-form-invalid-feedback>
                      </validation-provider>
                      <validation-provider
                        v-slot="{valid, invalid, errors, dirty, validated, validate}"
                        ref="attachmentValidation"
                        :rules="`size:${maxImageSizeKB}|mimes:${validMimeTypes}`"
                      >
                        <b-form-group :label="$t('Upload Attachment')" label-for="attachment-input">
                          <input
                            ref="fileUpload"
                            type="file"
                            style="display:none;"
                            :state="(dirty || validated) ? (valid ? null : false) : null"
                            @change="onFileSelected($event) || validate($event)"
                          >
                          <b-input-group>
                            <p :class="['form-control', (validated && valid) ? 'is-valid' : (invalid ? 'is-invalid' : '')]">
                              {{ uploadedFileName }}
                            </p>
                            <b-input-group-append>
                              <b-button size="sm" @click="$refs.fileUpload.click()">
                                {{ $t('Upload') }}
                              </b-button>
                            </b-input-group-append>
                          </b-input-group>
                          <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                            {{ errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </div>
                  </div>
                  <validation-provider v-slot="{valid, invalid, errors, dirty, validated}" rules="required">
                    <b-form-group :label="$t('The nature of your enquiry / question *')" label-for="message-input">
                      <b-form-textarea
                        id="message-input"
                        v-model="message"
                        rows="6"
                        :state="(dirty || validated) ? (valid ? null : false) : null"
                      />
                      <b-form-invalid-feedback :state="invalid" :force-show="invalid">
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </div>
                <div class="col-12 col-lg-6 order-0 order-lg-1">
                  <div class="row">
                    <div class="col-12 col-xxl-10 offset-xxl-2 col-xl-11 offset-xl-1">
                      <b-alert variant="warning" :show="true">
                        <strong>{{ $t('Please note:') }}</strong>
                        <ol class="small pt-3 pl-0">
                          <li class="mt-3">
                            <i18n path="helpCentreMsg">
                              <template v-slot:helpCentreLinkText>
                                <div></div>
                              </template>
                            </i18n>
                          </li>
                          <li>
                            <i18n path="reportingMsg">
                              <template v-slot:reportingLinkText>
                                <router-link class="text-link" to="/my-leads">
                                  {{ $t('reportingLinkText') }}
                                </router-link>
                              </template>
                            </i18n>
                          </li>
                        </ol>
                      </b-alert>
                    </div>
                  </div>
                </div>
              </div>
              <b-button size="sm" variant="primary" :disabled="isLoading" type="submit">
                {{ $t('Submit') }}
              </b-button>
            </validation-observer>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeading from "@/components/control/PageHeading";
import Spinner from "@/components/animation/Spinner";
import notification from "@/imports/mixins/notification";
import {ValidationObserver, ValidationProvider} from 'vee-validate';

const validMimeTypes = [
  {ext: 'png', mime: 'image/png'},
  {ext: 'jpg', mime: 'image/jpeg'},
  {ext: 'pdf', mime: 'application/pdf'},
  {ext: 'gif', mime: 'image/gif'},
  {ext: 'doc', mime: 'application/msword'},
  {ext: 'zip', mime: 'application/zip'},
  {ext: 'csv', mime: 'text/csv'},
  {ext: 'docx', mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'},
  {ext: 'xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'},
  {ext: 'txt', mime: 'text/plain'},
  {ext: 'ics', mime: 'text/calendar'},
];

export default {
  name: "Contact",
  components: {PageHeading, Spinner, ValidationObserver, ValidationProvider},
  mixins: [notification],
  data() {
    return {
      firstName: this.$store.state.company.COMPANY.company_user.forename,
      lastName: this.$store.state.company.COMPANY.company_user.surname,
      email: this.$store.state.company.COMPANY.company_user.email,
      contactNumber: this.$store.state.company.COMPANY.phone ?? this.$store.state.company.COMPANY.mobile ?? '',
      companyName: this.$store.state.company.COMPANY.internal_name,
      message: '',
      selectedFile: null,
      isLoading: false,
      busy: false,
      businessUnitContactPhone: this.$store.state.businessUnitContactDetails.BUSINESS_UNIT_CONTACT_DETAILS.phone,
    }
  },
  computed: {
    uploadedFileName() {
      return this.selectedFile !== null ? this.selectedFile.name : '';
    },
    maxImageSizeKB: function() {
      return this.$store.state.MAX_UPLOAD_SIZE / 1024;
    },
    validMimeTypes: function() {
      return validMimeTypes.map(({mime}) => mime).join(',');
    },
    pageTitle() {
      return this.$t('Contact');
    },
  },
  methods: {
    onFileSelected(e) {
      this.selectedFile = e.target.files[0];
    },
    onFormSubmit() {
      this.$refs.validationObserver.validate().then(success => {
        if (!success) {
          return;
        }

        this.isLoading = true;
        this.$api.contact.submit(this.firstName, this.lastName, this.email, this.contactNumber, this.companyName, this.message, this.selectedFile)
          .then(() => {
            this.notify(this.$bvToast, this.$t('Your message has been sent'), null, 'success');
            this.message = '';
            this.$refs.fileUpload.value = '';
            this.selectedFile = null;
            this.$refs.attachmentValidation.syncValue();
            this.$refs.attachmentValidation.reset();
            this.$refs.validationObserver.reset();
          })
          .catch(() => {
            this.notify(this.$bvToast, this.$t('There was an error sending your message'), null, 'danger')
          })
          .finally(() => this.isLoading = false);
      });
    },
  }
}
</script>

<style scoped lang="scss">
::v-deep .alert-warning {
  ol {
    list-style-position: inside;
  }
}

.custom-control {
  padding-left: 3rem;
}

.call-info {
  align-items: center;

  .separator {
    margin-right: 1rem;
    &:before {
      right: -15px;
    }
  }
  h2 {
    margin-left: .5rem;
  }

  @media only screen and (max-width: 374px) {
    flex-direction: column;
    align-items: flex-end;
    .separator {
      margin-right: 0;
      margin-bottom: 10px;

      &:before {
        content: none;
      }
    }
  }
}
</style>
